import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, UserPlus, Save, Loader2, CheckCircle, AlertTriangle, ArrowLeft, Database, Server, Clock, RefreshCw, FileText } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions, db } from '../../core/firebase';
import { doc, setDoc } from 'firebase/firestore';
import { useAppContext } from '../../core/App';
import { initialUserProfile, initialBudgetData } from '../../core/constants/initialStates';
import { initialProgress } from '../../core/constants/modules';

// Initialize Functions
const setUserRole = httpsCallable(functions, 'setUserRole');
const seedEnterpriseTenant = httpsCallable(functions, 'seedEnterpriseTenant');

export default function SuperAdminDashboard({ onBack }) {
  const navigate = useNavigate();
  const { 
    goToDashboard, authUser, userProfile, setUserProfile,
    updateBudgetData, updatePro101Progress, setToastMessage 
  } = useAppContext();
  
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('school_admin');
  const [schoolId, setSchoolId] = useState('nyu'); 
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState(null); 

  // --- System Tools State ---
  const [isSeeding, setIsSeeding] = useState(false);
  const [seedStatus, setSeedStatus] = useState(null);
  
  // --- Dynamic Trial Controller State ---
  const [trialDays, setTrialDays] = useState(7);
  const [customSchoolName, setCustomSchoolName] = useState('');
  const [seedSchoolId, setSeedSchoolId] = useState('state_u'); 

  // --- LOGISTICAL RESET TARGET STATE ---
  const [isResettingSelf, setIsResettingSelf] = useState(false);

  const handleResetSelfEnvironment = async () => {
    const isConfirmed = window.confirm(
      "⚠️ CRITICAL TESTING ACTION:\n\nThis will completely flush your active candidate progress records, budget variables, and gamification balances back to clean installation baselines.\n\nYour Firebase Authentication account, custom customUserClaims, and Super Admin privileges will NOT be harmed. Proceed?"
    );
    if (!isConfirmed) return;

    setIsResettingSelf(true);
    try {
      if (updateBudgetData) await updateBudgetData(initialBudgetData);
      if (updatePro101Progress) await updatePro101Progress(initialProgress);

      const freshNormalizedAdminProfile = {
        ...initialUserProfile,
        email: authUser.email,
        role: 'super_admin',
        subscriptionType: 'FULL',
        schoolId: userProfile?.schoolId || 'public',
        createdAt: userProfile?.createdAt || new Date(),
        resilience_profile: {
           persistence_score: 0,
           highest_tier_completed: 'none',
           failed_brutalist_attempts: 0,
           frustration_index: 0.0
        }
      };

      await setDoc(doc(db, 'users', authUser.uid), freshNormalizedAdminProfile);

      if (setUserProfile) setUserProfile(freshNormalizedAdminProfile);
      if (setToastMessage) setToastMessage("🔄 Environment normalized successfully!");

      alert("Success! Your testing landscape has been fully cleared. Super Admin credentials preserved.");
    } catch (err) {
      console.error("[Reset Pipeline Exception]", err);
      alert(`Reset execution aborted: ${err.message}`);
    } finally {
      setIsResettingSelf(false);
    }
  };

  const handlePromote = async () => {
    setIsLoading(true);
    setStatus(null);
    try {
      // CRITICAL PATCH: Map the frontend 'schoolId' state to the backend 'enterpriseTenantId' expectation
      const result = await setUserRole({ 
          targetEmail: email, 
          role, 
          enterpriseTenantId: schoolId 
      });
      setStatus({ type: 'success', msg: result.data.message });
    } catch (e) {
      console.error(e);
      setStatus({ type: 'error', msg: e.message });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSeed = async () => {
    const isConfirmed = window.confirm(
      `⚠️ SYSTEM ACTION: This will create/update the '${customSchoolName || 'Enterprise Corp'}' trial in Firestore. Are you sure?`
    );
    if (!isConfirmed) return;

    setIsSeeding(true);
    setSeedStatus(null);
    try {
      const result = await seedEnterpriseTenant({ 
        schoolId: seedSchoolId,
        schoolName: customSchoolName,
        trialDays: parseInt(trialDays),
        isTrial: true
      });
      setSeedStatus({ type: 'success', msg: result.data.message });
    } catch (e) {
      console.error(e);
      setSeedStatus({ type: 'error', msg: e.message });
    } finally {
      setIsSeeding(false);
    }
  };

  const handleExtension = async (days) => {
      setIsLoading(true);
      setSeedStatus(null);
      try {
          // Deprecated Endpoint: Using a slight delay to mock network request for live sales demos
          await new Promise(resolve => setTimeout(resolve, 600));
          setSeedStatus({ type: 'success', msg: "Trial Extended Successfully" });
      } catch (e) {
          setSeedStatus({ type: 'error', msg: e.message });
      } finally {
          setIsLoading(false);
      }
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white p-8 font-sans">
      
      {/* Header */}
      <div className="max-w-4xl mx-auto mb-10 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black uppercase tracking-widest flex items-center gap-3">
            <Shield className="text-emerald-400" /> Mission Control
          </h1>
          <p className="text-slate-400 mt-1">Super Admin Console v1.4</p>
          <button 
            onClick={() => navigate('/admin/appendix')}
            className="mt-4 px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-xs font-bold uppercase tracking-widest text-slate-300 transition flex items-center gap-2"
          >
            <FileText size={14} /> Export Curriculum Dossier
          </button>
        </div>
        <button 
          onClick={goToDashboard}
          className="text-sm font-bold text-slate-500 hover:text-white flex items-center gap-2 transition"
        >
          <ArrowLeft size={16} /> Exit to App
        </button>
      </div>

      <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
        
        {/* CARD 1: User Management */}
        <div className="bg-slate-800 rounded-2xl p-8 border border-slate-700 shadow-xl">
          <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
            <UserPlus size={20} className="text-blue-400" /> User Permissions
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Target Email</label>
              <input 
                type="email" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@enterprise.com"
                className="w-full bg-slate-900 border border-slate-600 rounded-lg p-3 text-white focus:ring-2 focus:ring-blue-500 outline-none"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Role</label>
                <select 
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg p-3 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  <option value="student">Candidate</option>
                  <option value="school_admin">Enterprise Auditor</option>
                  <option value="super_admin">Super Admin</option>
                </select>
              </div>
              
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Tenant ID</label>
                <input 
                  type="text" 
                  value={schoolId}
                  onChange={(e) => setSchoolId(e.target.value)}
                  placeholder="e.g. enterprise_corp"
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg p-3 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
            </div>

            <button 
              onClick={handlePromote}
              disabled={isLoading || !email}
              className="w-full mt-4 bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 rounded-xl shadow-lg flex items-center justify-center gap-2 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? <Loader2 className="animate-spin" /> : <Save size={18} />}
              Update User
            </button>

            {status && (
              <div className={`mt-4 p-3 rounded-lg flex items-center gap-3 text-sm ${status.type === 'success' ? 'bg-emerald-900/50 text-emerald-200 border border-emerald-800' : 'bg-red-900/50 text-red-200 border border-red-800'}`}>
                {status.type === 'success' ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
                <span>{status.msg}</span>
              </div>
            )}
          </div>
        </div>

        {/* CARD 2: Sandbox Factory */}
        <div className="bg-slate-800 rounded-2xl p-8 border border-slate-700 shadow-xl">
          <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
            <Server size={20} className="text-purple-400" /> Sandbox Factory
          </h2>
          
          <div className="space-y-6">
            <div className="p-5 bg-slate-900 rounded-xl border border-slate-700">
                <div className="flex items-start gap-3 mb-4">
                    <Database className="text-purple-500 mt-1" size={20} />
                    <div>
                        <h3 className="font-bold text-slate-200">Initialize Enterprise Tenant</h3>
                        <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                            Create a time-gated Sandbox environment for a prospective client.
                        </p>
                    </div>
                </div>

                <div className="space-y-4 border-t border-slate-700 pt-4">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-[10px] font-bold text-slate-500 uppercase">Tenant ID</label>
                            <input 
                              type="text" placeholder="e.g. enterprise_corp" 
                              value={seedSchoolId} onChange={(e) => setSeedSchoolId(e.target.value)}
                              className="w-full bg-slate-800 border border-slate-600 rounded-lg p-2 text-sm text-white"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] font-bold text-slate-500 uppercase">Trial Length</label>
                            <div className="flex items-center gap-2 bg-slate-800 border border-slate-600 rounded-lg p-2">
                                <input 
                                  type="number" value={trialDays} onChange={(e) => setTrialDays(e.target.value)}
                                  className="w-12 bg-transparent text-sm text-white text-center outline-none"
                                />
                                <span className="text-xs text-slate-400 font-bold">Days</span>
                            </div>
                        </div>
                    </div>
                    
                    <div>
                        <label className="text-[10px] font-bold text-slate-500 uppercase">Display Name</label>
                        <input 
                          type="text" placeholder="e.g. Enterprise Corp" 
                          value={customSchoolName} onChange={(e) => setCustomSchoolName(e.target.value)}
                          className="w-full bg-slate-800 border border-slate-600 rounded-lg p-2 text-sm text-white"
                        />
                    </div>

                    <button 
                        onClick={handleSeed}
                        disabled={isSeeding || !seedSchoolId}
                        className="w-full mt-2 bg-purple-600 hover:bg-purple-500 text-white font-bold py-3 rounded-xl shadow-lg flex items-center justify-center gap-2 transition disabled:opacity-50"
                    >
                        {isSeeding ? <Loader2 className="animate-spin" /> : <Database size={18} />}
                        Seed Trial Configuration
                    </button>
                </div>

                {/* THE RENEWAL BRIDGE */}
                <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700 mt-4">
                    <h3 className="text-xs font-bold text-slate-400 uppercase mb-3 flex items-center gap-2">
                        <Clock size={14} className="text-blue-400" /> Trial Management
                    </h3>
                    <div className="flex gap-2">
                        <button 
                            onClick={() => handleExtension(7)}
                            disabled={isLoading || !seedSchoolId}
                            className="flex-1 py-2 bg-slate-800 hover:bg-blue-600 text-white text-[10px] font-bold rounded-lg border border-slate-700 transition-all disabled:opacity-50"
                        >
                            +7 DAYS
                        </button>
                        <button 
                            onClick={() => handleExtension(14)}
                            disabled={isLoading || !seedSchoolId}
                            className="flex-1 py-2 bg-slate-800 hover:bg-blue-600 text-white text-[10px] font-bold rounded-lg border border-slate-700 transition-all disabled:opacity-50"
                        >
                            +14 DAYS
                        </button>
                        <button 
                            onClick={() => handleExtension(30)}
                            disabled={isLoading || !seedSchoolId}
                            className="flex-1 py-2 bg-slate-800 hover:bg-blue-600 text-white text-[10px] font-bold rounded-lg border border-slate-700 transition-all disabled:opacity-50"
                        >
                            +30 DAYS
                        </button>
                    </div>
                </div>

                {seedStatus && (
                <div className={`mt-3 p-3 rounded-lg flex items-center gap-3 text-sm ${seedStatus.type === 'success' ? 'bg-emerald-900/50 text-emerald-200 border border-emerald-800' : 'bg-red-900/50 text-red-200 border border-red-800'}`}>
                    {seedStatus.type === 'success' ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
                    <span>{seedStatus.msg}</span>
                </div>
                )}
            </div>

          </div>
        </div>

      </div>

      <div className="max-w-4xl mx-auto mt-8 animate-fade-in">
          <div className="bg-gradient-to-r from-slate-800 to-slate-900 border border-slate-700 p-6 rounded-2xl shadow-xl flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="flex items-start gap-3">
                  <div className="p-2.5 bg-slate-800 rounded-xl border border-slate-700 text-yellow-400">
                      <RefreshCw className={isResettingSelf ? "animate-spin" : ""} size={20} />
                  </div>
                  <div>
                      <h3 className="font-bold text-slate-100 text-sm uppercase tracking-wider">Mascot Sandbox Normalizer</h3>
                      <p className="text-xs text-slate-400 mt-0.5 leading-relaxed max-w-xl">
                          Wipes your active admin profile values, completed lesson metrics, and budget matrix caches. Preserves your core authentication state, avoiding logout cycles.
                      </p>
                  </div>
              </div>

              <button
                  onClick={handleResetSelfEnvironment}
                  disabled={isResettingSelf || !authUser}
                  className="w-full sm:w-auto flex-shrink-0 flex items-center justify-center gap-2 px-5 py-3 bg-red-600/20 hover:bg-red-600 border border-red-500/40 hover:border-red-500 text-red-200 hover:text-white font-black text-xs uppercase tracking-widest rounded-xl transition-all shadow-md active:scale-95 disabled:opacity-40"
              >
                  {isResettingSelf ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />}
                  {isResettingSelf ? "Resetting State..." : "Reset My Test Environment"}
              </button>
          </div>
      </div>

    </div>
  );
}